import { cloneVNode, defineComponent } from 'vue';
import PropTypes from '../_util/vue-types';
import { flattenChildren, getPropsSlot } from '../_util/props-util';
import warning from '../_util/warning';
import BreadcrumbItem from './BreadcrumbItem';
import BreadcrumbSeparator from './BreadcrumbSeparator';
import Menu from '../menu';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import { useConfigContextInject } from '../config-provider/context';
import useStyle from './style';
import { arrayType } from '../_util/type';
import classNames from '../_util/classNames';
export const breadcrumbProps = () => ({
    prefixCls: String,
    /** @deprecated Please use `items` instead */
    routes: { type: Array },
    params: PropTypes.any,
    separator: PropTypes.any,
    /** Breadcrumb items (antd ≥ 5.3). */
    items: arrayType(),
    itemRender: {
        type: Function,
    },
});
function getBreadcrumbName(route, params) {
    var _a;
    const name = (_a = route.title) !== null && _a !== void 0 ? _a : route.breadcrumbName;
    if (name == null || typeof name !== 'string') {
        return name !== null && name !== void 0 ? name : null;
    }
    const paramsKeys = Object.keys(params || {}).join('|');
    if (!paramsKeys) {
        return name;
    }
    return name.replace(new RegExp(`:(${paramsKeys})`, 'g'), (replacement, key) => params[key] || replacement);
}
function defaultItemRender(opt) {
    const { route, params, routes, paths } = opt;
    const isLastItem = routes.indexOf(route) === routes.length - 1;
    const name = getBreadcrumbName(route, params);
    if (isLastItem) {
        return <span>{name}</span>;
    }
    if (route.href !== undefined) {
        return <a href={route.href}>{name}</a>;
    }
    return <a href={`#/${paths.join('/')}`}>{name}</a>;
}
function normalizeRoutes(routes = []) {
    return routes.map(route => {
        var _a;
        return (Object.assign(Object.assign({}, route), { title: route.breadcrumbName, children: (_a = route.children) === null || _a === void 0 ? void 0 : _a.map(child => (Object.assign(Object.assign({}, child), { title: child.breadcrumbName }))) }));
    });
}
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'ABreadcrumb',
    inheritAttrs: false,
    props: breadcrumbProps(),
    slots: Object,
    setup(props, { slots, attrs }) {
        const { prefixCls, direction } = useConfigInject('breadcrumb', props);
        const { breadcrumb: ctxBreadcrumb } = useConfigContextInject();
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const getPath = (path, params) => {
            path = (path || '').replace(/^\//, '');
            Object.keys(params || {}).forEach(key => {
                path = path.replace(`:${key}`, params[key]);
            });
            return path;
        };
        const addChildPath = (paths, childPath, params) => {
            const originalPaths = [...paths];
            const path = getPath(childPath || '', params);
            if (path) {
                originalPaths.push(path);
            }
            return originalPaths;
        };
        const genForItems = ({ items = [], params = {}, separator, itemRender = defaultItemRender, }) => {
            const paths = [];
            return items.map((item, index) => {
                var _a;
                if (item.type === 'separator') {
                    return <BreadcrumbSeparator key={`sep-${index}`}>{item.separator}</BreadcrumbSeparator>;
                }
                const path = getPath(item.path || '', params);
                if (path) {
                    paths.push(path);
                }
                const tempPaths = [...paths];
                let overlay = null;
                if (item.menu) {
                    overlay = <Menu {...item.menu}/>;
                }
                else if (item.overlay) {
                    overlay = item.overlay;
                }
                else if (item.children && item.children.length) {
                    overlay = (<Menu items={item.children.map((child, i) => {
                            var _a, _b, _c;
                            return ({
                                key: (_c = (_b = (_a = child.key) !== null && _a !== void 0 ? _a : child.path) !== null && _b !== void 0 ? _b : child.breadcrumbName) !== null && _c !== void 0 ? _c : i,
                                label: itemRender({
                                    route: child,
                                    params,
                                    routes: items,
                                    paths: addChildPath(tempPaths, child.path || '', params),
                                }),
                            });
                        })}/>);
                }
                const itemProps = {
                    separator,
                    dropdownProps: item.dropdownProps,
                };
                if (overlay) {
                    itemProps.overlay = overlay;
                }
                if (item.href !== undefined) {
                    itemProps.href = item.href;
                }
                if (item.onClick) {
                    itemProps.onClick = item.onClick;
                }
                return (<BreadcrumbItem {...itemProps} key={(_a = item.key) !== null && _a !== void 0 ? _a : (path || item.breadcrumbName || index)} class={item.className}>
            {itemRender({ route: item, params, routes: items, paths: tempPaths })}
          </BreadcrumbItem>);
            });
        };
        return () => {
            var _a, _b;
            let crumbs;
            const { routes, params = {}, items } = props;
            const children = flattenChildren(getPropsSlot(slots, props));
            const breadcrumbCfg = ctxBreadcrumb === null || ctxBreadcrumb === void 0 ? void 0 : ctxBreadcrumb.value;
            const separator = (_b = (_a = getPropsSlot(slots, props, 'separator')) !== null && _a !== void 0 ? _a : breadcrumbCfg === null || breadcrumbCfg === void 0 ? void 0 : breadcrumbCfg.separator) !== null && _b !== void 0 ? _b : '/';
            const itemRender = props.itemRender || slots.itemRender || defaultItemRender;
            const mergedItems = (items === null || items === void 0 ? void 0 : items.length) ? items : (routes === null || routes === void 0 ? void 0 : routes.length) ? normalizeRoutes(routes) : null;
            if (mergedItems && mergedItems.length > 0) {
                crumbs = genForItems({
                    items: mergedItems,
                    params,
                    separator,
                    itemRender,
                });
            }
            else if (children.length) {
                crumbs = children.map((element, index) => {
                    warning(typeof element.type === 'object' &&
                        (element.type.__ANT_BREADCRUMB_ITEM || element.type.__ANT_BREADCRUMB_SEPARATOR), 'Breadcrumb', "Only accepts Breadcrumb.Item and Breadcrumb.Separator as it's children");
                    return cloneVNode(element, { separator, key: index });
                });
            }
            const breadcrumbClassName = classNames(prefixCls.value, {
                [`${prefixCls.value}-rtl`]: direction.value === 'rtl',
                [hashId.value]: true,
            }, breadcrumbCfg === null || breadcrumbCfg === void 0 ? void 0 : breadcrumbCfg.className, attrs.class);
            return wrapSSR(<nav {...attrs} class={breadcrumbClassName} style={[breadcrumbCfg === null || breadcrumbCfg === void 0 ? void 0 : breadcrumbCfg.style, attrs.style]}>
          <ol>{crumbs}</ol>
        </nav>);
        };
    },
});
